import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X, Trash2 } from 'lucide-react';
import { GoalType, Goal } from '../types';

interface GoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingGoal?: Goal | null;
}

export default function GoalModal({ isOpen, onClose, userId, householdId, editingGoal }: GoalModalProps) {
  const [type, setType] = useState<GoalType>('savings');
  const [name, setName] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [currentAmount, setCurrentAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  useEffect(() => {
    setIsConfirmingDelete(false);
    if (editingGoal) {
      setType(editingGoal.type);
      setName(editingGoal.name);
      setTargetAmount(editingGoal.targetAmount.toString());
      setCurrentAmount(editingGoal.currentAmount.toString());
    } else {
      setType('savings');
      setName('');
      setTargetAmount('');
      setCurrentAmount('');
    }
  }, [editingGoal, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetAmount || isNaN(Number(targetAmount))) return;
    
    setLoading(true);
    try {
      if (editingGoal && editingGoal.id) {
        await updateDoc(doc(db, 'goals', editingGoal.id), {
          type,
          name,
          targetAmount: Number(targetAmount),
          currentAmount: Number(currentAmount) || 0,
        });
      } else {
        await addDoc(collection(db, 'goals'), {
          userId,
          householdId,
          type,
          name,
          targetAmount: Number(targetAmount),
          currentAmount: Number(currentAmount) || 0,
          createdAt: serverTimestamp()
        });
      }
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingGoal || !editingGoal.id) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'goals', editingGoal.id));
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">
          {editingGoal ? 'Edit Financial Goal' : 'Add Financial Goal'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="savings"
                  checked={type === 'savings'}
                  onChange={() => setType('savings')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Savings Goal</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="debt"
                  checked={type === 'debt'}
                  onChange={() => setType('debt')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Debt Reduction</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Goal Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={type === 'savings' ? "e.g. Emergency Fund" : "e.g. Credit Card"}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Target Amount</label>
            <input
              type="number"
              step="0.01"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Current Balance / Progress</label>
            <input
              type="number"
              step="0.01"
              value={currentAmount}
              onChange={(e) => setCurrentAmount(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="0.00"
            />
          </div>

          <div className="flex gap-3 pt-2">
            {editingGoal && (
              isConfirmingDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-rose-600 font-medium">Delete?</span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-3 py-1.5 bg-rose-600 text-white rounded-md font-semibold text-xs hover:bg-rose-700 disabled:opacity-50"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsConfirmingDelete(false)}
                    disabled={loading}
                    className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-md font-semibold text-xs hover:bg-slate-300 disabled:opacity-50"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsConfirmingDelete(true)}
                  disabled={loading}
                  className="px-4 py-2.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-md font-semibold text-sm transition-colors flex items-center justify-center disabled:opacity-50"
                  title="Delete Goal"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )
            )}
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-md font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-colors"
            >
              {loading ? 'Saving...' : (editingGoal ? 'Save Changes' : 'Add Goal')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
